/**
 * API Client Utility
 * Provides centralized API communication with error handling
 */

import type { Address, PropertyFilePreview } from '@property-intel/shared';

// Empty default → calls go same-origin (CloudFront proxies /api/* to API
// Gateway). Local dev that hits a remote API still sets VITE_API_BASE_URL.
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

/** Max retries for transient failures */
const MAX_RETRIES = 2;
const RETRY_DELAY_MS = 1000;
const RETRYABLE_STATUS_CODES = [502, 503, 504];

/**
 * Fetch with automatic retry for transient failures.
 * Retries on network errors and 502/503/504 status codes.
 */
async function fetchWithRetry(input: RequestInfo | URL, init?: RequestInit, retries = MAX_RETRIES): Promise<Response> {
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const response = await fetch(input, init);
      if (RETRYABLE_STATUS_CODES.includes(response.status) && attempt < retries) {
        await new Promise(resolve => setTimeout(resolve, RETRY_DELAY_MS * (attempt + 1)));
        continue;
      }
      return response;
    } catch (error) {
      // Network error (offline, DNS failure, etc.)
      if (attempt < retries) {
        await new Promise(resolve => setTimeout(resolve, RETRY_DELAY_MS * (attempt + 1)));
        continue;
      }
      throw new ApiClientError(
        'Network error — please check your connection and try again.',
        'NETWORK_ERROR'
      );
    }
  }
  // Should not reach here, but TypeScript needs it
  throw new ApiClientError('Request failed after retries', 'RETRY_EXHAUSTED');
}

interface ApiError {
  code: string;
  message: string;
  details?: Record<string, any>;
  timestamp: string;
}

export class ApiClientError extends Error {
  constructor(
    message: string,
    public code: string,
    public details?: Record<string, any>
  ) {
    super(message);
    this.name = 'ApiClientError';
  }
}

/**
 * Thrown by `getPropertyFile` when the backend returns 404 — i.e. no
 * PROPERTY partition exists for the supplied propertyId. Callers render
 * the property-not-found UX (see PropertyFile.tsx) rather than treating
 * this as a generic API error.
 */
export class PropertyNotFoundError extends ApiClientError {
  constructor(propertyId: string) {
    super(
      `Property not found: ${propertyId}`,
      'PROPERTY_NOT_FOUND'
    );
    this.name = 'PropertyNotFoundError';
  }
}

/**
 * Product SKU discriminator — matches the backend's ProductType. Pair with
 * the canonical per-SKU amounts (7900 pence for compliance_pack, 999 pence
 * for planning_report) that the backend resolves server-side from this
 * value alone.
 */
export type CheckoutProductType = 'compliance_pack' | 'planning_report';

/** Request body for POST /payment/checkout-session. */
export interface CreateCheckoutSessionInput {
  productType: CheckoutProductType;
  productId: string;
}

/**
 * Success response from POST /payment/checkout-session. The caller
 * redirects the browser to `url`; `sessionId` is surfaced for logging
 * and URL round-trips via `{CHECKOUT_SESSION_ID}` — it is NOT proof of
 * payment (Req 8.x — polling on `isPaid` is the only payment gate).
 */
export interface CreateCheckoutSessionResponse {
  url: string;
  sessionId: string;
}

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    let errorData: ApiError;
    try {
      errorData = await response.json();
    } catch {
      throw new ApiClientError(
        `HTTP ${response.status}: ${response.statusText}`,
        'HTTP_ERROR'
      );
    }

    throw new ApiClientError(
      errorData.message || 'An error occurred',
      errorData.code || 'UNKNOWN_ERROR',
      errorData.details
    );
  }

  // Handle 204 No Content
  if (response.status === 204) {
    return {} as T;
  }

  return response.json();
}

/**
 * Server-side Seller's Pack draft (Phase B3).
 *
 * The wizard hydrates from this on mount (so a returning seller picks up
 * where they left off, even from a different device) and PUTs back on
 * every formData change (debounced). 404 from GET means "no draft yet" —
 * that's expected on first visit.
 */
export interface SellerDraftRecord {
  userId: string;
  propertyId: string;
  formData: Record<string, unknown>;
  updatedAt: string;
  createdAt: string;
}

export const api = {
  // Seller's Pack draft persistence ───────────────────────────────────────────
  async getSellerDraft(propertyId: string): Promise<SellerDraftRecord | null> {
    const response = await fetch(
      `${API_BASE_URL}/api/seller-drafts/${encodeURIComponent(propertyId)}`,
      { credentials: 'include' }
    );
    if (response.status === 404) return null;
    if (!response.ok) {
      throw new ApiClientError('Failed to load draft', 'LOAD_DRAFT_ERROR');
    }
    return response.json();
  },

  async putSellerDraft(
    propertyId: string,
    formData: Record<string, unknown>
  ): Promise<SellerDraftRecord> {
    const response = await fetch(
      `${API_BASE_URL}/api/seller-drafts/${encodeURIComponent(propertyId)}`,
      {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ formData }),
      }
    );
    if (!response.ok) {
      throw new ApiClientError('Failed to save draft', 'SAVE_DRAFT_ERROR');
    }
    return response.json();
  },

  async deleteSellerDraft(propertyId: string): Promise<void> {
    const response = await fetch(
      `${API_BASE_URL}/api/seller-drafts/${encodeURIComponent(propertyId)}`,
      { method: 'DELETE', credentials: 'include' }
    );
    if (!response.ok && response.status !== 404) {
      throw new ApiClientError('Failed to delete draft', 'DELETE_DRAFT_ERROR');
    }
  },

  // Property File (Workstream 4) ─────────────────────────────────────────────
  /**
   * Resolve an address to a property partition. Anonymous-friendly. The
   * backend canonicalises the address, looks up an existing PROPERTY
   * record, and creates one if none exists. Used by Home + by the
   * `/planning-check?address=…` legacy redirect.
   */
  async lookupProperty(
    address: Address
  ): Promise<{ propertyId: string; address: Address; created: boolean }> {
    const response = await fetchWithRetry(`${API_BASE_URL}/api/property/lookup`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ address }),
    });
    return handleResponse<{ propertyId: string; address: Address; created: boolean }>(response);
  },

  /**
   * Read the public property-file preview. Anonymous-friendly. Throws
   * {@link PropertyNotFoundError} on 404 so callers can render the
   * "we couldn't find public records for this address" empty state
   * without inspecting raw error codes.
   */
  async getPropertyFile(
    propertyId: string
  ): Promise<{ propertyId: string; address: Address; provenance: string; sections: PropertyFilePreview }> {
    const response = await fetchWithRetry(
      `${API_BASE_URL}/api/property/${encodeURIComponent(propertyId)}`
    );
    if (response.status === 404) {
      throw new PropertyNotFoundError(propertyId);
    }
    return handleResponse<{
      propertyId: string;
      address: Address;
      provenance: string;
      sections: PropertyFilePreview;
    }>(response);
  },

  // Address Lookup
  async lookupAddress(postcode: string) {
    const response = await fetchWithRetry(`${API_BASE_URL}/api/address/lookup`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ postcode }),
    });
    return handleResponse<{ addresses: any[]; count: number }>(response);
  },

  // Planning Reports
  async createPlanningReport(address: any, userId: string) {
    const response = await fetchWithRetry(`${API_BASE_URL}/api/planning/report`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ address, userId }),
    });
    return handleResponse<{ reportId: string; transactionId: string; previewData: any }>(response);
  },

  async getPlanningReport(reportId: string) {
    const response = await fetchWithRetry(`${API_BASE_URL}/api/planning/report/${reportId}`);
    return handleResponse<{ report: any; isPaid: boolean; previewData?: any }>(response);
  },

  async downloadPlanningReportPDF(reportId: string) {
    const response = await fetchWithRetry(`${API_BASE_URL}/api/planning/report/${reportId}/pdf`);
    return handleResponse<{ downloadUrl: string; expiresIn: number; fileName: string }>(response);
  },

  // Document Upload
  async getUploadUrl(transactionId: string, fileName: string, fileType: string) {
    const response = await fetchWithRetry(`${API_BASE_URL}/api/document/upload-url`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ transactionId, fileName, fileType }),
    });
    return handleResponse<{ uploadUrl: string; documentId: string }>(response);
  },

  async processDocument(documentId: string, documentType: string) {
    const response = await fetchWithRetry(`${API_BASE_URL}/api/document/${documentId}/process`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ documentType }),
    });
    return handleResponse<{ status: string; extractedText?: string }>(response);
  },

  // Selling Documents Upload APIs
  async uploadSellingDocument(sessionId: string, documentTypeId: string, file: File) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('sessionId', sessionId);
    formData.append('documentTypeId', documentTypeId);

    const response = await fetchWithRetry(`${API_BASE_URL}/api/selling-documents/upload`, {
      method: 'POST',
      credentials: 'include',
      body: formData,
    });
    return handleResponse<{
      success: boolean;
      document: {
        id: string;
        documentTypeId: string;
        fileName: string;
        fileSize: number;
        s3Key: string;
        uploadedAt: string;
      };
      uploadUrl?: string; // Pre-signed S3 URL for direct upload
    }>(response);
  },

  async markDocumentUnavailable(sessionId: string, documentTypeId: string) {
    const response = await fetchWithRetry(`${API_BASE_URL}/api/selling-documents/mark-unavailable`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sessionId, documentTypeId }),
    });
    return handleResponse<{ success: boolean }>(response);
  },

  async deleteSellingDocument(sessionId: string, documentId: string) {
    const response = await fetchWithRetry(`${API_BASE_URL}/api/selling-documents/document/${documentId}`, {
      method: 'DELETE',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sessionId }),
    });
    return handleResponse<{ success: boolean }>(response);
  },

  async getSessionState(sessionId: string) {
    const response = await fetchWithRetry(`${API_BASE_URL}/api/selling-documents/session/${sessionId}`, {
      credentials: 'include',
    });
    return handleResponse<{
      session: {
        sessionId: string;
        tenure: 'freehold' | 'leasehold';
        alterationsMade: boolean;
        address?: any;
        documents: any[];
        completionPercentage: number;
        createdAt: string;
        expiresAt: string;
      };
    }>(response);
  },

  async formatDisclosureAnswers(sessionId: string, answers: Record<string, string>, questions: { questionId: string; questionText: string; sectionTitle: string }[]) {
    const response = await fetchWithRetry(`${API_BASE_URL}/api/disclosure/format`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sessionId, answers, questions }),
    });
    return handleResponse<{
      success: boolean;
      pairs: { questionId: string; raw: string; formatted: string | null }[];
      stats: { total: number; formatted: number; passedThrough: number };
    }>(response);
  },

  async generateCompliancePack(sessionId: string, options?: { disclosureAnswers?: Record<string, string>; address?: string; tenure?: 'freehold' | 'leasehold'; acceptanceDecisions?: Record<string, 'raw' | 'formatted'> }) {
    const response = await fetchWithRetry(`${API_BASE_URL}/api/selling-documents/generate`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sessionId, ...options }),
    });
    return handleResponse<{
      success: boolean;
      pack: {
        packId: string;
        forms: any[];
        missingDocuments: string[];
        completionPercentage: number;
        generatedAt: string;
      };
    }>(response);
  },

  async downloadDocument(sessionId: string, documentId: string) {
    const response = await fetchWithRetry(
      `${API_BASE_URL}/api/selling-documents/document/${documentId}/download?sessionId=${sessionId}`,
      { credentials: 'include' }
    );
    return handleResponse<{
      downloadUrl: string;
      expiresIn: number;
      document: {
        id: string;
        fileName: string;
        documentType: string;
      };
    }>(response);
  },

  async downloadCompliancePack(sessionId: string) {
    const response = await fetchWithRetry(
      `${API_BASE_URL}/api/selling-documents/pack/${sessionId}/download`,
      { credentials: 'include' }
    );
    return handleResponse<{
      downloadUrl: string;
      expiresIn: number;
      packId: string;
    }>(response);
  },

  async deleteSession(sessionId: string) {
    const response = await fetchWithRetry(`${API_BASE_URL}/api/selling-documents/session/${sessionId}`, {
      method: 'DELETE',
      credentials: 'include',
    });
    return handleResponse<{
      success: boolean;
      deletedCounts: {
        s3Objects: number;
        documents: number;
        provenance: number;
      };
    }>(response);
  },

  async getDocumentStatus(documentId: string) {
    const response = await fetchWithRetry(`${API_BASE_URL}/api/document/${documentId}/status`);
    return handleResponse<{ status: string; progress?: number }>(response);
  },

  // Compliance Pack
  async createCompliancePack(
    transactionId: string,
    userRole: 'buyer' | 'seller',
    propertyType: 'freehold' | 'leasehold',
    documentIds: string[]
  ) {
    const response = await fetchWithRetry(`${API_BASE_URL}/api/compliance/pack`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ transactionId, userRole, propertyType, documentIds }),
    });
    return handleResponse<{ packId: string; estimatedCompletionTime: number }>(response);
  },

  async getCompliancePack(packId: string) {
    const response = await fetchWithRetry(`${API_BASE_URL}/api/compliance/pack/${packId}`);
    return handleResponse<{ pack: any; isPaid: boolean }>(response);
  },

  async downloadCompliancePackPDF(packId: string) {
    const response = await fetchWithRetry(`${API_BASE_URL}/api/compliance/pack/${packId}/pdf`);
    if (!response.ok) {
      const errorData = await response.json();
      throw new ApiClientError(
        errorData.error?.message || 'Failed to download PDF',
        errorData.error?.code || 'DOWNLOAD_ERROR'
      );
    }
    return response.blob();
  },

  async deleteCompliancePack(packId: string) {
    const response = await fetchWithRetry(`${API_BASE_URL}/api/packs/${packId}`, {
      method: 'DELETE',
      credentials: 'include',
    });
    return handleResponse<{ success: boolean; message: string }>(response);
  },

  // Payment
  /**
   * Create a Stripe Checkout Session and return the hosted-page URL.
   *
   * Stripe Checkout hosts card entry on Stripe's own page rather than
   * embedding Stripe Elements in ours. Amount is resolved server-side
   * from `productType` alone; any `amount` field in a hand-rolled body
   * would be discarded by the backend (Req 3.3), so we don't expose one
   * here.
   *
   * Caller flow:
   *   const { url } = await api.createCheckoutSession({ productType, productId });
   *   window.location.assign(url);
   *
   * Validates: Requirements 16.2, 17.2.
   */
  async createCheckoutSession(
    input: CreateCheckoutSessionInput
  ): Promise<CreateCheckoutSessionResponse> {
    const response = await fetchWithRetry(`${API_BASE_URL}/api/payment/checkout-session`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });
    return handleResponse<CreateCheckoutSessionResponse>(response);
  },

  // Terms Acceptance
  async getTermsStatus() {
    const response = await fetchWithRetry(`${API_BASE_URL}/api/user/terms-status`, {
      credentials: 'include',
    });
    return handleResponse<{
      tosVersion: string | null;
      privacyPolicyVersion: string | null;
      tosAcceptedAt: string | null;
      privacyPolicyAcceptedAt: string | null;
    }>(response);
  },

  async acceptTerms(tosVersion: string, privacyPolicyVersion: string) {
    const response = await fetchWithRetry(`${API_BASE_URL}/api/user/accept-terms`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tosVersion, privacyPolicyVersion }),
    });
    return handleResponse<{ accepted: boolean; acceptedAt: string }>(response);
  },

  // User Documents
  async getUserDocuments() {
    const response = await fetchWithRetry(`${API_BASE_URL}/api/user/documents`);
    return handleResponse<{ documents: any[] }>(response);
  },

  async downloadUserDocument(documentId: string) {
    const response = await fetchWithRetry(`${API_BASE_URL}/api/user/documents/${documentId}/download`);
    return handleResponse<{ downloadUrl?: string; restorationRequested?: boolean }>(response);
  },

  // Field Reconciliation Wizard (Feature 35)
  async listFields(packId: string) {
    const response = await fetchWithRetry(
      `${API_BASE_URL}/api/compliance-packs/${encodeURIComponent(packId)}/fields`,
      { credentials: 'include' }
    );
    return handleResponse<{
      fields: ReconciliationField[];
      progress: { confirmed: number; total: number };
    }>(response);
  },

  async confirmField(
    packId: string,
    fieldId: string,
    body: { action: 'accept' | 'edit' | 'rewrite'; value?: string; originalValue?: string }
  ) {
    const response = await fetchWithRetry(
      `${API_BASE_URL}/api/compliance-packs/${encodeURIComponent(
        packId
      )}/fields/${encodeURIComponent(fieldId)}/confirm`,
      {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      }
    );
    return handleResponse<{
      field: ReconciliationField;
      progress: { confirmed: number; total: number };
    }>(response);
  },

  async rewriteField(packId: string, fieldId: string, value: string) {
    const response = await fetchWithRetry(
      `${API_BASE_URL}/api/compliance-packs/${encodeURIComponent(
        packId
      )}/fields/${encodeURIComponent(fieldId)}/rewrite`,
      {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ value }),
      }
    );
    return handleResponse<{
      original: string;
      rewritten: string;
      confidence: number;
      factualDeltas: string[];
    }>(response);
  },
};

/**
 * Public shape of a Field record returned by the reconciliation API. Mirrors
 * the backend's `Field` interface in `packages/shared/src/types/index.ts`,
 * but kept locally on the frontend so consumers don't need a runtime
 * dependency on the shared package's compiled output.
 */
export interface ReconciliationField {
  fieldId: string;
  packId: string;
  formType: 'TA6' | 'TA7' | 'LPE1';
  fieldKey: string;
  section: string;
  questionPlainEnglish: string;
  state: 'auto_filled' | 'needs_review' | 'needs_user_input' | 'confirmed';
  value: string | null;
  originalValue: string | null;
  sourceDocumentId: string | null;
  sourceDocumentName: string | null;
  sourcePageRef: string | null;
  confidence: number | null;
  required: boolean;
  extractedAt: string | null;
  confirmedAt: string | null;
  confirmedBy: string | null;
}
